/**
 * Inspiration Page — 旅行灵感
 *
 * 功能：推荐目的地、推荐POI、个性化旅行建议
 * 技术：Recommendation API + TanStack Query
 */

import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Sparkles, MapPin, Star } from 'lucide-react';
import { useRecommendDestinations, useRecommendPois } from '@/hooks/useRecommendations';
import { RecommendationCard } from '@/components/recommendation/RecommendationCard';
import { useAuth } from '@/hooks/useAuth';

const TRAVEL_THEMES = [
  { icon: '🌊', name: '海岛旅行', desc: '阳光、沙滩、海浪' },
  { icon: '☕', name: '咖啡城市', desc: '文艺慢生活' },
  { icon: '🏔', name: '山川路线', desc: '自然与远方' },
  { icon: '🎎', name: '文化体验', desc: '历史与传承' },
  { icon: '🍜', name: '美食之旅', desc: '舌尖上的旅行' },
  { icon: '🌃', name: '都市探索', desc: '繁华与霓虹' },
];

const Inspiration = () => {
  const { user } = useAuth();
  const destinationsQuery = useRecommendDestinations(user?.id, undefined, 8);
  const poisQuery = useRecommendPois(user?.id, undefined, 8);

  const isLoading = destinationsQuery.isLoading || poisQuery.isLoading;
  const hasError = destinationsQuery.isError || poisQuery.isError;

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-20">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1 mb-4 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
            <Sparkles className="w-3 h-3" /> AI 旅行灵感
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-2">发现你的下一次旅行</h1>
          <p className="text-text-secondary">基于你的旅行偏好，为你推荐目的地和体验</p>
        </div>

        {/* Travel Themes */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-text-primary mb-4">探索主题</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {TRAVEL_THEMES.map((theme) => (
              <div
                key={theme.name}
                className="p-4 rounded-xl bg-white shadow-sm border border-gray-50 hover:shadow-md hover:border-primary/20 transition-all cursor-pointer text-center"
              >
                <span className="text-2xl">{theme.icon}</span>
                <h4 className="text-sm font-medium text-text-primary mt-2">{theme.name}</h4>
                <p className="text-xs text-gray-400 mt-1">{theme.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2 text-gray-500">正在生成灵感...</span>
          </div>
        )}

        {/* Error State */}
        {hasError && !isLoading && (
          <div className="text-center py-16">
            <p className="text-gray-400 mb-4">推荐服务暂时不可用</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="w-4 h-4 mr-1" /> 重试
            </Button>
          </div>
        )}

        {/* Recommended Destinations */}
        {!isLoading && !hasError && (
          <>
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" /> 推荐目的地
                </h2>
                <Button variant="ghost" size="sm" onClick={() => destinationsQuery.refetch()}>
                  <RefreshCw className="w-3 h-3 mr-1" /> 刷新
                </Button>
              </div>

              {destinationsQuery.data && destinationsQuery.data.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {destinationsQuery.data.map((dest, idx) => (
                    <RecommendationCard key={idx} type="destination" data={dest} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl">
                  <p className="text-gray-400">暂无推荐目的地</p>
                  <p className="text-xs text-gray-300 mt-1">登录后获取个性化推荐</p>
                </div>
              )}
            </div>

            {/* Recommended POIs */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary" /> 推荐体验
                </h2>
                <Button variant="ghost" size="sm" onClick={() => poisQuery.refetch()}>
                  <RefreshCw className="w-3 h-3 mr-1" /> 刷新
                </Button>
              </div>

              {poisQuery.data && poisQuery.data.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {poisQuery.data.map((poi, idx) => (
                    <RecommendationCard key={idx} type="poi" data={poi} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl">
                  <p className="text-gray-400">暂无推荐体验</p>
                  <p className="text-xs text-gray-300 mt-1">开始探索后获取智能推荐</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* AI Tips */}
        <div className="p-6 rounded-2xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/10">
          <h3 className="font-medium text-text-primary mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> AI 旅行助手
          </h3>
          <p className="text-sm text-text-secondary">
            随着你搜索和浏览更多内容，AI 会学习你的旅行偏好，为你提供更精准的个性化推荐。
            每次旅行规划都会被记住，让下一次出行更加轻松。
          </p>
        </div>
      </div>
    </div>
  );
};

export default Inspiration;
